import * as React from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface ReworkBadgeProps {
  /** Derived-флаг из snapshot'а записи (§1.2) — клиент ничего не вычисляет. */
  isRework: boolean;
  /** Идентификатор доработки (`rework_note`, §1.1) — вторичным текстом, если введён. */
  note?: string | null;
  className?: string;
}

/**
 * ReworkBadge — компактная пометка «доработка» на строке записи исполнителя.
 * Единый маркер на обе поверхности вне мастера (задача 115 §2.2): лента дня
 * `/field` (`EntryRow`) и строка дневного редактора (`TimesheetDayEditor`).
 * Источник — derived `is_rework` из snapshot'а (§1.2); клиент не считает.
 *
 * Тон — `info` (синька, informational): доработка легальна, не caution — в паре
 * с баннером редактора (§2.1). Идентификатор (`rework_note`) — вторичным
 * muted-текстом рядом с бейджем, если введён. Для обычной записи
 * (`is_rework=false`) не рендерится — один бейдж на строку, без дублей.
 *
 * Соответствует `patterns.md §Badges`.
 */
export function ReworkBadge({
  isRework,
  note,
  className,
}: ReworkBadgeProps): React.ReactElement | null {
  const { t } = useTranslation();
  if (!isRework) return null;
  const trimmed = note?.trim() ? note.trim() : null;
  return (
    <span
      className={cn("inline-flex min-w-0 max-w-full items-center gap-1.5", className)}
      data-testid="rework-badge"
    >
      <Badge tone="info">{t("field.entryFlow.rework.badge")}</Badge>
      {trimmed && (
        <span
          className="min-w-0 truncate text-[11px] normal-case text-[var(--fg-3)]"
          data-testid="rework-note"
        >
          {trimmed}
        </span>
      )}
    </span>
  );
}
